import { MDX } from "./mdx/tokens";
import { BarChart3, TrendingUp, Landmark, Coins, Zap, FileText, Wallet } from "lucide-react";
import { Reveal } from "@/components/Reveal";

/**
 * ExchangeFootprint — Section 5.
 * Pan-African coverage stats. Replaces the prior 21-exchange ticker grid.
 */
const COVERAGE = [
    {
        icon:   BarChart3,
        stat:   "20",
        label:  "Stock Markets We Cover",
        note:   "Real share prices from across the continent",
    },
    {
        icon:   TrendingUp,
        stat:   "1,357+",
        label:  "Companies You Can Follow",
        note:   "Across every market we cover",
    },
    {
        icon:   Landmark,
        stat:   "17",
        label:  "Market Trend Trackers",
        note:   "Show how a whole market is doing, not just one company",
    },
    {
        icon:   Coins,
        stat:   "21",
        label:  "Currency Pairs",
        note:   "Live prices for African money against the US Dollar",
    },
    {
        icon:   Zap,
        stat:   "19",
        label:  "Raw Materials",
        note:   "Gold, oil, farm crops and more, tracked live",
    },
    {
        icon:   FileText,
        stat:   "7",
        label:  "Government Loans (Bonds)",
        note:   "Money governments borrow, with interest paid back over time",
    },
    {
        icon:   Wallet,
        stat:   "20",
        label:  "Short-Term Government Loans",
        note:   "Government borrowing that pays back in under a year",
    },
];

export default function ExchangeFootprint({ isDark }) {
    const bg = isDark ? "bg-[#090D16]" : "bg-white";
    const text = isDark ? "text-[#F9FAFB]" : "text-[#090D16]";
    const dim = isDark ? "text-[#9CA3AF]" : "text-[#6B7280]";
    const cardBg = isDark ? "bg-[#0F1524]" : "bg-white";
    const cardBrd = isDark ? "border-[#1F2937]" : "border-[#E5E7EB]";

    return (
        <section
            className={`${bg} ${text} py-16 md:py-24 border-t ${isDark ? "border-[#1F2937]" : "border-[#E5E7EB]"}`}
            data-testid="exchange-footprint"
        >
            <div className="max-w-[1200px] mx-auto px-4 md:px-8">
                <div className="mb-10">
                    <div className="font-inter text-xs font-bold uppercase tracking-widest mb-3" style={{ color: MDX.blueLow }}>
                        Where You Can Trade
                    </div>
                    <h2 className="font-inter font-medium leading-tight" style={{ fontSize: "28px" }}>
                        Real prices from markets across Africa.
                    </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4" data-testid="footprint-coverage-grid">
                    {COVERAGE.map((c, i) => (
                        <Reveal key={c.label} delay={i * 0.06} className="h-full">
                            <div
                                data-testid={`footprint-stat-${c.label.replace(/\s+/g, "-").toLowerCase()}`}
                                className={`h-full rounded-xl border ${cardBrd} ${cardBg} p-5 flex items-start gap-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md`}
                                onMouseEnter={(e) => (e.currentTarget.style.borderColor = MDX.blueLow)}
                                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "")}
                            >
                                <div
                                    className="shrink-0 rounded-lg p-2.5"
                                    style={{ background: MDX.tint10, color: MDX.blueLow }}
                                >
                                    <c.icon className="h-5 w-5" />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-inter font-bold proportional-nums leading-none" style={{ fontSize: "clamp(24px, 3.4vw, 32px)", color: MDX.blueLow }}>
                                        {c.stat}
                                    </div>
                                    <div className="font-inter font-bold text-[15px] leading-tight mt-1.5">{c.label}</div>
                                    <div className={`font-roboto text-[13px] leading-snug mt-1 ${dim}`}>{c.note}</div>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
